import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { KeyRound, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import "./aurora.css";
import { activateCode, checkCode, loadActivation, saveActivation, clearActivation, ACTIVATION_ERRORS } from "@/lib/activationApi";
import { loadState, saveState } from "@/components/superCerveau/scShared";

// Enregistre l'activation et pré-remplit les prénoms du jeu s'ils sont vides.
const applyActivation = (code, data) => {
  saveActivation({ code, label: data.label || "", child_name: data.child_name || "", parent_name: data.parent_name || "", message: data.message || "", activated_at: new Date().toISOString() });
  const st = loadState();
  const setup = { ...st.setup };
  if (!setup.childName && data.child_name) setup.childName = data.child_name;
  if (!setup.parentName && data.parent_name) setup.parentName = data.parent_name;
  saveState({ ...st, setup });
};

const Shell = ({ children, testid }) => (
  <Layout className="sc-aurora">
    <div className="max-w-xl mx-auto py-10 animate-fade-up" data-testid={testid}>
      <div className="rounded-3xl bg-white/80 border border-[var(--border)] p-6 sm:p-8 text-center space-y-4">{children}</div>
    </div>
  </Layout>
);

// Route /p/super-cerveau/a/:code — active le module sur cet appareil puis redirige vers le jeu.
export function ScActivate() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  useEffect(() => {
    activateCode(code).then((d) => {
      if (!d.ok) { setState(d.state || "unknown"); return; }
      applyActivation(code, d); setData(d); setState("ok");
    }).catch(() => setState("network"));
  }, [code]);

  if (state === "loading") return <Shell testid="sc-activate-loading"><div className="text-5xl">🧠</div><p className="text-[var(--ink-soft)]">Activation de Mission « Super-Cerveau »…</p></Shell>;
  if (state === "ok") return (
    <Shell testid="sc-activate-ok">
      <ShieldCheck size={40} className="mx-auto text-[#2F7A55]" />
      <h1 className="font-serif-title text-3xl text-[var(--ink)]">Module activé !</h1>
      <p className="text-[var(--ink)]">Mission « Super-Cerveau » est maintenant disponible sur cet appareil{data.child_name ? ` pour ${data.child_name}` : ""}.</p>
      {data.message && <div className="rounded-2xl bg-[var(--sand)] p-4 text-sm text-[var(--ink)] italic text-left" data-testid="sc-activate-message">💬 Message de votre praticien : « {data.message} »</div>}
      <p className="text-xs text-[var(--ink-soft)]">Astuce : ajoutez ensuite le jeu à l'écran d'accueil de la tablette (bloc « Retrouver le jeu »).</p>
      <button onClick={() => navigate("/p/super-cerveau", { replace: true })} data-testid="sc-activate-go" className="px-8 py-3 rounded-full bg-[var(--gold)] text-white font-bold shadow-sm hover:brightness-105 active:scale-95 transition-all">Ouvrir le jeu</button>
    </Shell>
  );
  return (
    <Shell testid="sc-activate-error">
      <ShieldAlert size={40} className="mx-auto text-[#B4483A]" />
      <h1 className="font-serif-title text-3xl text-[var(--ink)]">Lien non valide</h1>
      <p className="text-[var(--ink)]" data-testid="sc-activate-error-text">{state === "network" ? "Connexion impossible pour le moment. Réessayez dans quelques instants." : ACTIVATION_ERRORS[state] || ACTIVATION_ERRORS.unknown}</p>
    </Shell>
  );
}

// Porte d'entrée du module public : exige une activation enregistrée sur l'appareil et encore valide.
export function ScActivationGate({ children }) {
  const [status, setStatus] = useState(() => (loadActivation() ? "checking" : "missing"));
  const [reason, setReason] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status !== "checking") return;
    const a = loadActivation();
    checkCode(a.code).then((d) => {
      if (d.valid) setStatus("ok");
      else { clearActivation(); setReason(ACTIVATION_ERRORS[d.state] || ACTIVATION_ERRORS.unknown); setStatus("missing"); }
    }).catch(() => setStatus("ok")); // hors ligne : on laisse jouer, revérifié à la prochaine connexion
  }, [status]);

  const submit = async () => {
    const c = code.trim().split("/").pop();
    if (!c) return;
    setBusy(true);
    try {
      const d = await activateCode(c);
      if (!d.ok) { setReason(ACTIVATION_ERRORS[d.state] || ACTIVATION_ERRORS.unknown); return; }
      applyActivation(c, d); toast.success("Module activé sur cet appareil !"); setStatus("ok");
    } catch { setReason("Connexion impossible pour le moment. Réessayez dans quelques instants."); } finally { setBusy(false); }
  };

  if (status === "ok") return children;
  if (status === "checking") return <Shell testid="sc-gate-checking"><div className="text-5xl">🧠</div><p className="text-[var(--ink-soft)]">Vérification de l'activation…</p></Shell>;
  return (
    <Shell testid="sc-gate">
      <KeyRound size={40} className="mx-auto text-[var(--gold)]" />
      <h1 className="font-serif-title text-3xl text-[var(--ink)]">Mission « Super-Cerveau »</h1>
      <p className="text-[var(--ink)]">Ce module est proposé <strong>sur prescription</strong> : il s'active avec le lien unique remis par votre praticien.</p>
      {reason && <div className="rounded-2xl bg-[#FDE4DF] text-[#B4483A] text-sm p-3" data-testid="sc-gate-reason">{reason}</div>}
      <div className="text-left space-y-2">
        <label className="text-sm font-semibold text-[var(--ink)]" htmlFor="sc-gate-code">Vous avez reçu un lien ou un code ? Collez-le ici :</label>
        <div className="flex gap-2">
          <input id="sc-gate-code" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Code d'activation ou lien complet" data-testid="sc-gate-code"
            className="flex-1 rounded-full border border-[var(--border)] bg-white px-4 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
          <button onClick={submit} disabled={busy || !code.trim()} data-testid="sc-gate-submit" className="px-5 py-2.5 rounded-full bg-[var(--gold)] text-white text-sm font-bold disabled:opacity-50 active:scale-95 transition-all">{busy ? "…" : "Activer"}</button>
        </div>
      </div>
      <p className="text-xs text-[var(--ink-soft)]">Le lien s'ouvre directement sur l'appareil de la famille (tablette ou téléphone) ; les données du jeu restent sur cet appareil.</p>
    </Shell>
  );
}
